import * as React from "react"
import { Check, Copy } from "lucide-react"
import { Button } from "./button"
import { cn } from "../../lib/utils"

export interface CopyableTextProps {
  /** Value to copy to clipboard on click */
  value: string
  /** Optional class name for the trigger */
  className?: string
  /** Label / content to display (e.g. the text or a <code> node) */
  children: React.ReactNode
}

export function CopyableText({
  value,
  className,
  children,
}: CopyableTextProps) {
  const [copied, setCopied] = React.useState(false)
  const resetTimeoutRef = React.useRef<number | null>(null)

  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation()
    navigator.clipboard.writeText(value).then(() => {
      if (resetTimeoutRef.current !== null) {
        window.clearTimeout(resetTimeoutRef.current)
      }

      setCopied(true)

      resetTimeoutRef.current = window.setTimeout(() => {
        setCopied(false)
      }, 3000)
    })
  }

  React.useEffect(() => {
    return () => {
      if (resetTimeoutRef.current !== null) {
        window.clearTimeout(resetTimeoutRef.current)
      }
    }
  }, [])

  return (
    <Button
      type="button"
      variant="ghost"
      size="sm"
      onClick={handleClick}
      className={cn(
        "relative group h-auto justify-start pr-6 hover:bg-transparent hover:text-primary",
        "inline-flex w-auto pe-[26px]",
        className
      )}
    >
      {children}
      <span
        aria-hidden="true"
        className="pointer-events-none absolute ltr:right-2 rtl:left-2 top-1/2 -translate-y-1/2 flex items-center justify-center"
      >
        <Copy
          className={cn(
            "h-3 w-3 transition-opacity duration-150",
            copied ? "opacity-0" : "opacity-0 group-hover:opacity-100"
          )}
        />
        <Check
          className={cn(
            "absolute h-3 w-3 text-success transition-opacity duration-150",
            copied ? "opacity-100" : "opacity-0"
          )}
        />
      </span>
    </Button>
  )
}
